import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { CopyIcon, ArrowsClockwiseIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import Grid from '@/components/inputs/CustomGrid';
import CustomButton from '@/components/inputs/CustomButton';
import CustomTextField from '@/components/inputs/CustomTextField';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useConfirm from '@/components/dialogs/hooks/useConfirm';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const CompanySettings = ({ companyId: companyIdProp, embedded = false }) => {
  const params = useParams();
  const companyId = companyIdProp || params.companyId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(true);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { openConfirm, ConfirmDialog } = useConfirm();

  const [company, setCompany] = useState(null);
  const [generatedToken, setGeneratedToken] = useState('');
  const [templates, setTemplates] = useState({
    customerHeaderText: '', sellerHeaderText: '',
    invoiceHeader: '', invoiceFooter: '', invoiceNotes: '',
    emailSubject: '', emailBody: '',
  });
  const [customFieldsOriginal, setCustomFieldsOriginal] = useState([]);
  const [customFieldsCopy, setCustomFieldsCopy] = useState([]);
  const [nextInvoiceNumberInput, setNextInvoiceNumberInput] = useState('');
  const [savingNextNumber, setSavingNextNumber] = useState(false);
  const [maxInvoiceNumberUsed, setMaxInvoiceNumberUsed] = useState(0);
  const [smtp, setSmtp] = useState({
    host: '', port: 465, secure: true,
    user: '', pass: '', fromName: '', fromEmail: '',
  });

  useEffect(() => {
    const load = async () => {
      const res = await Connections.postRequest(ApiEndpoints.companyGet, { companyId });
      if (res.ok) {
        const c = res.data.company;
        setCompany(c);
        setTemplates(c.templates || {});
        setCustomFieldsOriginal(c.settings?.customFieldsOriginal || []);
        setCustomFieldsCopy(c.settings?.customFieldsCopy || []);
        setNextInvoiceNumberInput(String(c.settings?.nextInvoiceNumber ?? 1));
        setMaxInvoiceNumberUsed(res.data.maxInvoiceNumberUsed || 0);
        const s = c.settings?.smtp || {};
        setSmtp({
          host: s.host || '',
          port: s.port ?? 465,
          secure: s.secure ?? true,
          user: s.user || '',
          pass: '',
          fromName: s.fromName || '',
          fromEmail: s.fromEmail || '',
        });
      }
      setLoading(false);
    };
    load().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleRotateToken = async () => {
    const confirmed = await openConfirm(t('settings.rotateTokenConfirm'));
    if (!confirmed) return;

    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.companyRotateToken, { companyId });
    if (res.ok) {
      setGeneratedToken(res.data.token);
      createSuccessSnackbar(t('settings.tokenGenerated'));
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const copyToken = () => {
    navigator.clipboard.writeText(generatedToken);
    createSuccessSnackbar(t('settings.tokenCopied'));
  };

  const handleTemplateChange = ({ name, value }) => {
    setTemplates(prev => ({ ...prev, [name]: value }));
  };

  const addCustomField = type => {
    const setter = type === 'original' ? setCustomFieldsOriginal : setCustomFieldsCopy;
    setter(prev => [...prev, { label: '', value: '' }]);
  };

  const removeCustomField = (type, index) => {
    const setter = type === 'original' ? setCustomFieldsOriginal : setCustomFieldsCopy;
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const updateCustomField = (type, index, field, value) => {
    const setter = type === 'original' ? setCustomFieldsOriginal : setCustomFieldsCopy;
    setter(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.companyUpdate, {
      companyId,
      templates,
      settings: { customFieldsOriginal, customFieldsCopy },
    });
    if (res.ok) {
      createSuccessSnackbar(t('settings.saved'));
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const handleSmtpChange = ({ name, value }) => {
    setSmtp(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveSmtp = async () => {
    setLoading(true);
    const smtpToSend = {
      host: smtp.host || '',
      port: Number(smtp.port) || 465,
      secure: !!smtp.secure,
      user: smtp.user || '',
      fromName: smtp.fromName || '',
      fromEmail: smtp.fromEmail || '',
      ...(smtp.pass ? { pass: smtp.pass } : {}),
    };
    const res = await Connections.postRequest(ApiEndpoints.companyUpdate, {
      companyId,
      settings: { smtp: smtp.host ? smtpToSend : {} },
    });
    if (res.ok) {
      createSuccessSnackbar(t('settings.smtpSaved'));
      setSmtp(prev => ({ ...prev, pass: '' }));
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const handleClearSmtp = async () => {
    const confirmed = await openConfirm(t('settings.smtpClearConfirm'));
    if (!confirmed) return;

    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.companyUpdate, {
      companyId,
      settings: { smtp: {} },
    });
    if (res.ok) {
      createSuccessSnackbar(t('settings.smtpCleared'));
      setSmtp({
        host: '', port: 465, secure: true,
        user: '', pass: '', fromName: '', fromEmail: '',
      });
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const handleSaveNextInvoiceNumber = async () => {
    const parsed = parseInt(nextInvoiceNumberInput, 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
      createErrorSnackbar(t('settings.nextInvoiceNumberInvalid'));
      return;
    }

    setSavingNextNumber(true);
    const res = await Connections.postRequest(
      ApiEndpoints.companySetNextInvoiceNumber,
      { companyId, nextInvoiceNumber: parsed },
    );
    if (res.ok) {
      createSuccessSnackbar(t('settings.nextInvoiceNumberSaved'));
      setCompany(prev => ({
        ...prev,
        settings: { ...prev.settings, nextInvoiceNumber: parsed },
      }));
    } else if (res.errorCode === 'INVOICE_NUMBER_CONFLICT') {
      const maxUsed = res.errorData?.maxUsed;
      createErrorSnackbar(
        t('settings.nextInvoiceNumberConflict', { maxUsed, min: (maxUsed || 0) + 1 }),
      );
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setSavingNextNumber(false);
  };

  if (loading) return <Loading />;
  if (!company) return null;

  const currentNext = company.settings?.nextInvoiceNumber ?? 1;
  const parsedNextInput = parseInt(nextInvoiceNumberInput, 10);
  const inputBelowMaxUsed = maxInvoiceNumberUsed > 0
    && Number.isInteger(parsedNextInput)
    && parsedNextInput <= maxInvoiceNumberUsed;

  const renderCustomFields = (fields, type) => (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-sm font-semibold">
          {t(type === 'original' ? 'settings.customFieldsOriginal' : 'settings.customFieldsCopy')}
        </p>
        <button type="button" className={iconBtn} onClick={() => addCustomField(type)}>
          <PlusIcon size={16} />
        </button>
      </div>
      {fields.map((field, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="flex gap-2 mb-2 items-end">
          <div className="flex-1">
            <CustomTextField
              value={field.label} label={t('label')}
              onChange={({ value }) => updateCustomField(type, index, 'label', value)}
            />
          </div>
          <div className="flex-1">
            <CustomTextField
              value={field.value} label={t('value')}
              onChange={({ value }) => updateCustomField(type, index, 'value', value)}
            />
          </div>
          <button
            type="button"
            className={`${iconBtn} hover:text-destructive`}
            onClick={() => removeCustomField(type, index)}
          >
            <TrashIcon size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{t('app.title')} | {t('settings')} - {company.name}</title>
        </Helmet>
      )}
      <ConfirmDialog />

      {!embedded && (
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">{t('settings')} - {company.name}</h1>
        </div>
      )}

      {/* API Token */}
      <Card className="p-6 mb-6">
        <h2 className="text-base font-semibold mb-2">{t('settings.apiToken')}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.apiTokenDescription')}</p>
        <div className="flex gap-3 items-center flex-wrap">
          <CustomButton onClick={handleRotateToken}>
            <ArrowsClockwiseIcon size={18} />
            {t(company.settings?.apiToken ? 'settings.rotateToken' : 'settings.generateToken')}
          </CustomButton>
          {company.settings?.apiToken && (
            <Badge variant="secondary" style={{ borderRadius: '0.375rem' }}>
              {t('settings.tokenActive')}
            </Badge>
          )}
        </div>
        {generatedToken && (
          <Alert variant="destructive" className="mt-4">
            <p className="text-sm font-semibold mb-2">{t('settings.tokenWarning')}</p>
            <div className="flex items-center gap-2">
              <code className="font-mono break-all flex-1 text-xs">{generatedToken}</code>
              <button type="button" className={iconBtn} onClick={copyToken}>
                <CopyIcon size={18} />
              </button>
            </div>
          </Alert>
        )}
      </Card>

      {/* Next invoice number */}
      <Card className="p-6 mb-6">
        <h2 className="text-base font-semibold mb-2">{t('settings.nextInvoiceNumber')}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.nextInvoiceNumberDescription', { current: currentNext })}
        </p>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="w-48">
            <CustomTextField
              fieldName="nextInvoiceNumber"
              value={nextInvoiceNumberInput}
              onChange={({ value }) => setNextInvoiceNumberInput(String(value))}
              type="number"
              fullWidth
              error={inputBelowMaxUsed ? 'settings.nextInvoiceNumberBelowCurrent' : ''}
            />
          </div>
          <CustomButton
            onClick={handleSaveNextInvoiceNumber}
            disabled={savingNextNumber || inputBelowMaxUsed || nextInvoiceNumberInput === ''}
          >
            {t('save')}
          </CustomButton>
        </div>
      </Card>

      {/* SMTP */}
      <Card className="p-6 mb-6">
        <h2 className="text-base font-semibold mb-2">{t('settings.smtp')}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.smtpDescription')}</p>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="host" label={t('settings.smtpHost')} value={smtp.host}
              onChange={handleSmtpChange} fullWidth
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <CustomTextField
              fieldName="port" label={t('settings.smtpPort')} value={smtp.port}
              onChange={handleSmtpChange} fullWidth type="number"
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none mt-7">
              <Checkbox
                checked={!!smtp.secure}
                onCheckedChange={checked => setSmtp(prev => ({ ...prev, secure: checked === true }))}
              />
              {t('settings.smtpSecure')}
            </label>
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="user" label={t('settings.smtpUser')} value={smtp.user}
              onChange={handleSmtpChange} fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="pass" label={t('settings.smtpPass')} value={smtp.pass}
              onChange={handleSmtpChange} fullWidth type="password"
              placeholder={t('settings.smtpPassPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="fromName" label={t('settings.smtpFromName')} value={smtp.fromName}
              onChange={handleSmtpChange} fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="fromEmail" label={t('settings.smtpFromEmail')} value={smtp.fromEmail}
              onChange={handleSmtpChange} fullWidth
            />
          </Grid>
        </Grid>
        <div className="flex justify-end gap-3 mt-4">
          {company.settings?.smtp?.host && (
            <CustomButton variant="outlined" color="error" onClick={handleClearSmtp}>
              {t('settings.smtpClear')}
            </CustomButton>
          )}
          <CustomButton onClick={handleSaveSmtp}>
            {t('save')}
          </CustomButton>
        </div>
      </Card>

      {/* Custom Fields */}
      <Card className="p-6 mb-6">
        <h2 className="text-base font-semibold mb-2">{t('settings.customFields')}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.customFieldsDescription')}</p>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>{renderCustomFields(customFieldsOriginal, 'original')}</Grid>
          <Grid item xs={12} md={6}>{renderCustomFields(customFieldsCopy, 'copy')}</Grid>
        </Grid>
      </Card>

      {/* Templates */}
      <Card className="p-6 mb-6">
        <h2 className="text-base font-semibold mb-4">{t('settings.templates')}</h2>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="customerHeaderText" label={t('settings.customerHeaderText')}
              value={templates.customerHeaderText} onChange={handleTemplateChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="sellerHeaderText" label={t('settings.sellerHeaderText')}
              value={templates.sellerHeaderText} onChange={handleTemplateChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="invoiceHeader" label={t('settings.invoiceHeader')}
              value={templates.invoiceHeader} onChange={handleTemplateChange}
              fullWidth multiline rows={3}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="invoiceFooter" label={t('settings.invoiceFooter')}
              value={templates.invoiceFooter} onChange={handleTemplateChange}
              fullWidth multiline rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              fieldName="invoiceNotes" label={t('settings.invoiceNotes')}
              value={templates.invoiceNotes} onChange={handleTemplateChange}
              fullWidth multiline rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomTextField
              fieldName="emailSubject" label={t('settings.emailSubject')}
              value={templates.emailSubject} onChange={handleTemplateChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              fieldName="emailBody" label={t('settings.emailBody')}
              value={templates.emailBody} onChange={handleTemplateChange}
              fullWidth multiline rows={4}
            />
          </Grid>
        </Grid>
      </Card>

      <div className="flex justify-end gap-3">
        {!embedded && (
          <CustomButton variant="outlined" onClick={() => navigate('/companies')}>
            {t('back')}
          </CustomButton>
        )}
        <CustomButton onClick={handleSaveSettings}>
          {t('save')}
        </CustomButton>
      </div>
    </>
  );
};

export default CompanySettings;
